import React from 'react';
import { Edit, CheckCircle, Cancel, Schedule } from '@mui/icons-material';
import { JobWithNotes, Appointment } from '../workItemTypes';
import { AppointmentsHandlers } from '../hooks/useAppointments';
import { formatAppointmentDateTime, formatTimestamp } from '../utils/workItemUtils';

export interface AppointmentsSectionProps {
  job: JobWithNotes;
  appointments: Appointment[];
  appointmentsHandlers: AppointmentsHandlers;
}

export const AppointmentsSection: React.FC<AppointmentsSectionProps> = ({
  job,
  appointments,
  appointmentsHandlers
}) => {
  // Filter appointments related to this job (by customer ID or other criteria)
  const relatedAppointments = appointments.filter(appointment => 
    appointment.customerId === job.customerId
  );

  // Sort appointments by start date (newest first)
  const sortedAppointments = relatedAppointments.sort((a, b) => {
    const aStartAt = a.startAt || a.createdAt;
    const bStartAt = b.startAt || b.createdAt;
    return new Date(bStartAt).getTime() - new Date(aStartAt).getTime();
  });

  if (sortedAppointments.length === 0) {
    return null;
  }

  return (
    <div className="workitem-details-section">
      <div className="section-header">
        <h4>Related Appointments</h4>
      </div>
      
      <div className="appointments-container">
        {sortedAppointments.map((appointment) => (
          <div key={appointment.id} className={`appointment ${appointment.status.toLowerCase()}`}>
            <div className="appointment-header">
              <div className="appointment-meta">
                <span className="appointment-datetime">
                  {formatAppointmentDateTime(appointment.startAt, appointment.endAt)}
                </span>
                <span className="appointment-owner">{appointment.assignedUsers || 'Unassigned'}</span>
                <span className="appointment-status">{appointment.status}</span>
              </div>
              <div className="appointment-actions">
                <button
                  type="button"
                  className="icon-btn"
                  onClick={() => appointmentsHandlers.handleEditAppointment(appointment)}
                  title="Edit appointment"
                >
                  <Edit style={{ fontSize: '16px' }} />
                </button>
                {appointment.status === 'scheduled' && (
                  <button
                    type="button"
                    className="icon-btn"
                    onClick={() => appointmentsHandlers.handleToggleAppointmentStatus(appointment)}
                    title="Mark as completed"
                  >
                    <CheckCircle style={{ fontSize: '16px' }} />
                  </button>
                )}
                {appointment.status === 'completed' && (
                  <button
                    type="button"
                    className="icon-btn"
                    onClick={() => appointmentsHandlers.handleToggleAppointmentStatus(appointment)}
                    title="Mark as scheduled"
                  >
                    <Schedule style={{ fontSize: '16px' }} />
                  </button>
                )}
                {appointment.status !== 'cancelled' && (
                  <button
                    type="button"
                    className="icon-btn cancel"
                    onClick={() => appointmentsHandlers.handleCancelAppointment(appointment)}
                    title="Cancel appointment"
                  >
                    <Cancel style={{ fontSize: '16px' }} />
                  </button>
                )}
              </div>
            </div>
            <div className="appointment-content">
              <div className="appointment-task">{appointment.title}</div>
              <div className="appointment-type">Type: {appointment.type}</div>
              {appointment.notes && (
                <div className="appointment-notes">{appointment.notes}</div>
              )}
              <div className="appointment-created">
                Created: {formatTimestamp(appointment.createdAt)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};